import ReactDOM from 'react-dom';
import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useContext,
  createContext,
  useLayoutEffect,
  useMemo,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Grid,
  Typography,
  Box,
  Autocomplete,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  useMediaQuery,
  Slide,
  Avatar,
  InputLabel,
  Select,
  MenuItem,
  Card,
  styled,
  Stack,
} from '@mui/material';
import {
  CloudUpload,
  Send as SendIcon,
  DeleteOutline,
  InsertEmoticon,
  Close,
  CloseRounded,
  MediaBluetoothOn,
  PermMedia,
  TravelExplore,
  AddLocationAlt,
  ArrowDropDown,
} from '@mui/icons-material'; // Import InsertEmoticon icon
import { useDispatch, useSelector } from 'react-redux';
import { usePostNewsMutation, useUpdateNewsMutation } from '../gnnApiSlice';
import { selectCurrentUser, selectCurrentUserEmpCode } from '../../Auth/authSlice';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker from 'emoji-picker-react'; // Import EmojiPicker
import AttachAvatar from '../../../common/AttachAvatar';
import { useAbility } from '@casl/react';
import { AbilityContext } from '../../../context/Ability';
import { subject } from '@casl/ability';
import { useTheme } from '@emotion/react';
import dev from '../../../assets/developers/dev.png';
import { selectSwipeEdge, toggleSwipe } from '../../Search/searchSlice';
import SwipeEdge from '../../../common/SwipeEdge';
import UserAvatar from '../../../common/UserAvatar';
import { useFetchProfileQuery } from '../../Profile/profileApiSlice';
import { countries, regions } from '../../../constants/region';
import {
  selectPostOptions,
  setCategories,
  setCountry,
  setRegions,
  setDescription,
  setAttachments,
  selectNewsUploadProgress,
  selectNewsUploadStarted,
} from '../gnnSlice';
import { quillFix } from '../../../utils/quillFix';
import { generateThumbnail } from '../../../utils/generateThumbnail';
import linkwayGif from '../../../assets/Linkway/LWGIF.gif';
import { variants } from '../../../variants/variants';
import SplashScreen from '../../../pages/SplashScreen';
import toast from 'react-hot-toast';
const StyledQuill = styled(ReactQuill)({
  '& .ql-container': {
    // height: 'calc(50vh)',
    // border: 'none',
    // border: 0,
    backgroundColor: '#ffffff',
    '&.ql-focused': {
      boxShadow: '#3f51b5 0 0 0 2px',
    },
  },
  // '& .ql-container.ql-snow': {
  //   height: 'auto',
  //   // border: 0,
  // },
  '& .ql-editor': {
    height: 'calc(100vh - 300px)',
    // overflowY: 'scroll',
  },
});
const videoFiletypes = /mp4|mkv|webm|mov|avi|wmv|flv|3gp|mp3|m4a|flac|hevc|heic|mov|quicktime/;

const PostNewsDialog = ({ open, onClose, newsData, isEdit, setNewsPosted, newsPosted }) => {
  const { country, regions: selectedRegions, categories, description, attachments } = useSelector(selectPostOptions);
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false); // State to manage emoji picker visibility
  const quillRef = useRef(null); // Ref to access the ReactQuill instance
  const currentUser = useSelector(selectCurrentUser);
  const [filePreviews, setFilePreviews] = useState([]);
  const [postNews, { isLoading: isPosting }] = usePostNewsMutation();
  const [updateNews, { isLoading: isUpdating }] = useUpdateNewsMutation();
  // const [description, setDescription] = useState('');
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(theme.breakpoints.down('md'));
  const isLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const empCode = useSelector(selectCurrentUserEmpCode);
  const { data: user, isLoading } = useFetchProfileQuery(empCode);
  const ability = useAbility(AbilityContext);
  const dispatch = useDispatch();
  const [showOverlay, setShowOverlay] = useState(false);
  const [oldAttachments, setOldAttachments] = useState([]);
  let maxVideoSize = 200 // in MB
  let maxImageSize = 20 // in MB
  let selectionLimitForMobile = 5
  let selectionLimitForWeb = 20
  const categoriesArray = useMemo(() => {
    const array = ['Internal Posts'];
    if (ability.can('create', subject('Gnn', { categories: 'Public news' })) || ability.can('manage', 'Gnn')) {
      array.push('Public news');
    }
    if (ability.can('create', subject('Gnn', { categories: 'Categories News' })) || ability.can('manage', 'Gnn')) {
      array.push(
        'Events',
        'Summer extravaganza',
        'GPL 2025',
        'GPL 2024',
        'GPL 2023',
        'R&R',
        'GIFT',
        'Foundation',
        'New Joinee',
        'Celebrations',
        'Business',
        'Corporate',
        'Achievements',
        'Team@Gateway',
        'Awards and Recognition',
        'Tech@Gateway',
        'Fun Friday',
        'Growth Story',
        'Milestone',
        'Sports Tournaments'
      );
    }
    return array;
  }, [ability]);
  useEffect(() => {
    if (!isEdit && quillRef?.current) quillRef.current.getEditor().root.innerHTML = description || '';
  }, [open]);
  useEffect(() => {
    if (categoriesArray.length === 1) {
      dispatch(setCategories(categoriesArray[0]));
    }
    if (isEdit && newsData) {
      if (quillRef?.current) quillRef.current.getEditor().root.innerHTML = newsData.description || '';
      dispatch(setCategories(newsData.categories[0] || 'Internal Posts'));
      dispatch(setDescription(newsData.description || ''));
      dispatch(setCountry(newsData.country || 'All'));
      dispatch(setRegions(newsData.regions || 'All'));

      setOldAttachments([...newsData.attachments]);
    }
  }, [categoriesArray, isEdit, newsData, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(toggleSwipe({ value: false }));
    };
  }, []);

  const handleFileChange = (e) => {
    try {
      const files = Array.from(e?.target?.files);
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'video/mp4'];
      const validFiles = files?.filter((file) => {
        if (file.type === 'video/mp4' && file.size > maxVideoSize * 1024 * 1024) {
          toast.error(`File size should be less than ${maxVideoSize}MB`);
          return;
        }
        if (file.type === 'image/*' && file.size > maxImageSize * 1024 * 1024) {
          toast.error(`File size should be less than ${maxImageSize}MB`);
          return;
        }
        return allowedTypes.includes(file?.type);
      });
      const newAttachments = [...attachments, ...validFiles].slice(0, selectionLimitForWeb);
      dispatch(setAttachments(newAttachments));

      const previews = newAttachments.map((file) => {
        const fileType = file?.type?.split('/')[0];
        const url = URL.createObjectURL(file);

        if (fileType === 'video') {
          return {
            type: 'video',
            url: url,
          };
        } else {
          return {
            type: 'image',
            url: url,
          };
        }
      });
      setFilePreviews(previews);
    } catch (error) {
      console.log('error in file change', error);
      return;
    }
  };
  // useEffect(() => {
  //   if (!isEdit && attachments.length > 0) {
  //     console.log(attachments, 'news');
  //     const previews = attachments?.map((file) => {
  //       const fileType = file.type.split('/')[0];
  //       return {
  //         type: fileType === 'video' ? 'video' : 'image',
  //         url: URL.createObjectURL(file),
  //       };
  //     });
  //     setFilePreviews(previews);
  //   }
  // }, [attachments]);

  const handleRemoveAttachment = (from, index) => {
    if (from == 'oldAttachments') {
      const dummyArray = [...oldAttachments];
      setOldAttachments(dummyArray.filter((_, i) => i !== index));
    } else {
      window['ReactNativeWebView'] && window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'removeImage',data:{index} }));
      const dummyArray = [...attachments];
      dispatch(setAttachments(dummyArray.filter((_, i) => i !== index)));
      const newPreviews = filePreviews.filter((_, i) => i !== index);
      setFilePreviews(newPreviews);
    }
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    const slicedText = quillFix(description);
    console.log('sliced', slicedText, description);
    formData.append('empCode', currentUser?.empCode);
    formData.append('categories', categories);
    formData.append('description', slicedText);
    formData.append('country', country);
    formData.append('regions', JSON.stringify(selectedRegions));

    if (attachments.length > 0) {
      attachments.forEach((file) => {
        formData.append('attachments[]', file);
      });
    }

    try {
      let response;
      if (isEdit && newsData && newsData._id) {
        const formDataEdit = new FormData();
        const slicedText = quillFix(description);
        formDataEdit.append('categories', categories);
        formDataEdit.append('description', slicedText);
        formDataEdit.append('country', country);
        formDataEdit.append('regions', JSON.stringify(selectedRegions));
        formDataEdit.append('oldAttachments', JSON.stringify(oldAttachments));
        if (attachments.length > 0) {
          attachments.forEach((file) => {
            formDataEdit.append('attachments[]', file);
          });
        }
        if (!isMd) {
          setShowOverlay(true);
          response = await updateNews({
            id: newsData._id,
            formData: formDataEdit,
            // formData: {
            //     categories,
            //     description: slicedText,
            //     country,
            //     regions: selectedRegions,
            //     attachments,
            //   },
          }).unwrap();
          setNewsPosted((prev) => prev + 1);
          setShowOverlay(false);
          return;
        }
        setShowOverlay(true);
        let value = {
          newsId: newsData._id,
          categories,
          description: slicedText,
          country,
          regions: selectedRegions,
          oldAttachments: oldAttachments,
        };
        dispatch(setAttachments([]));
        setFilePreviews([]);
        window['ReactNativeWebView'] &&
          window['ReactNativeWebView']?.postMessage(
            JSON.stringify({
              type: 'updatePost',
              value: value,
            })
          );
          // setShowOverlay(false);
      } else {
        if (isMd) {
          setShowOverlay(true);
          window['ReactNativeWebView'] &&
            window['ReactNativeWebView']?.postMessage(
              JSON.stringify({
                type: 'submit',
                value: {
                  empCode,
                  categories,
                  description: slicedText,
                  country,
                  regions: selectedRegions,
                },
              })
            );
          dispatch(setAttachments([]));
          setFilePreviews([]);
          // setNewsPosted((prev) => prev + 1);
          return;
        } else {
          setShowOverlay(true);
          response = await postNews(formData).unwrap();
          setNewsPosted((prev) => prev + 1);
          onClose();
          setShowOverlay(false);
          dispatch(toggleSwipe({ value: false }));
        }
        // setShowOverlay(true);
        // response = await postNews(formData).unwrap();
        // setNewsPosted((prev) => prev + 1);
        // setShowOverlay(false);
      }
      dispatch(setAttachments([]));
      setFilePreviews([]);
      onClose();
    } catch (error) {
      console.error('Error posting/updating news:', error);
    }
  };

  const handleEmojiClick = (emojiObject) => {
    const quillText = description; // Assuming you are using 'description' state for Quill content
    const trimmedAnnouncement =
      quillText.startsWith('<p><br></p>') || !quillText.includes('</p><p><br></p>')
        ? quillText.replaceAll('<p><br></p>', '').trim()
        : quillText.replaceAll('<p><br></p>', '<br>').trim();

    const formattedText = trimmedAnnouncement.endsWith('</p>')
      ? trimmedAnnouncement.slice(0, trimmedAnnouncement.length - 4) + emojiObject.emoji + '</p>'
      : trimmedAnnouncement + emojiObject.emoji;

    dispatch(setDescription(formattedText));
    setEmojiPickerOpen(false);
  };
  const slideTransition = {
    TransitionComponent: Slide,
    TransitionProps: {
      direction: 'up',
    },
  };
  const handleButtonClick = () => {
    dispatch(toggleSwipe({ value: true }));
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'swipe', value: true }));
  };
  const onSwipeClose = () => {
    dispatch(toggleSwipe({ value: false }));
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'swipe', value: false }));
  };
  const onPostClose = () => {
    dispatch(toggleSwipe({ value: false }));
    onClose();
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'swipe', value: false }));
  };
  const handleCountryChange = (newCountry) => {
    dispatch(setCountry(newCountry));
  };

  const handleCategoryChange = (newCategory) => {
    dispatch(setCategories(newCategory));
  };

  const handleRegionsChange = (newRegions) => {
    dispatch(setRegions(newRegions));
  };

  const handleUploadClick = () => {
    window['ReactNativeWebView'] && window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'attachment',data:{video:maxVideoSize,image:maxImageSize,selectionLimit:selectionLimitForMobile} }));

    // window['ReactNativeWebView'] && window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'permission' }));
  };
  const handleDescriptionChange = (description) => {
    dispatch(setDescription(description));
  };
  const handleCombineclose = () => {
    onPostClose();
    handleSubmit();
  };

  useEffect(() => {
    // Handle messages from React Native
    const handleMessage = (event) => {
      try {
        let message = JSON.parse(event.data);
        if (message?.type === 'selectAttachment') {
          const newAttachments = [...attachments, ...message?.value].slice(0, selectionLimitForMobile);
          dispatch(setAttachments(newAttachments));
          const previews = newAttachments.map((file) => {
            const fileType = file.type.split('/')[0];

            if (fileType === 'video') {
              return {
                type: 'video',
                url: file.url,
                name: file.fileName,
              };
            } else {
              return {
                type: 'image',
                url: file.url,
                name: file.fileName,
              };
            }
          });
          setFilePreviews(previews);
          window['ReactNativeWebView'] &&
            window['ReactNativeWebView']?.postMessage(
              JSON.stringify({
                type: 'testAttachment',
                value: previews,
              })
            );
        }
        if (message?.type === 'newPost') {
          setNewsPosted((prev) => prev + 1);
          setShowOverlay(false);
          window['ReactNativeWebView'] &&
            window['ReactNativeWebView']?.postMessage(
              JSON.stringify({
                type: 'testAttachment',
                value: 'posted bhai',
              })
            );
        }
      } catch (err) {
        window['ReactNativeWebView'] &&
          window['ReactNativeWebView']?.postMessage(
            JSON.stringify({
              type: 'error',
              value: event.data,
              message: err,
            })
          );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    // Handle messages from React Native
    const handleMessage = (event) => {
      if (event.data === 'postError') {
        setShowOverlay(false);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const swipeOpen = useSelector(selectSwipeEdge);
  const SwipeContent = () => {
    return (
      <Box
        sx={{
          display: 'grid',
          // flexDirection: 'column',
          placeItems: 'center',
          alignContent: 'start',
          gap: 2,
          mt: 1,
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: '12px',
          overflow: 'auto',
          height: 'auto',
        }}
      >
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold', mb: 2 }}>Who can see your Post?</Typography>
        <FormControl fullWidth>
          <InputLabel id="category-label">News Category *</InputLabel>
          <Select
            labelId="category-label"
            value={categories}
            onChange={(event) => handleCategoryChange(event.target.value)}
            disabled={categoriesArray.length === 1} // Disable if there's only one category
            label="News Category *"
            sx={{
              borderRadius: '8px',
              backgroundColor: 'white',
              '& .MuiSvgIcon-root': {
                color: 'black',
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200, // Set the max height for the menu
                },
              },
            }}
          >
            {categoriesArray.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel id="country-label">Country *</InputLabel>
          <Select
            labelId="country-label"
            label="Country *"
            id="country"
            name="country"
            value={country}
            onChange={(event) => {
              handleCountryChange(event.target.value);
            }}
            sx={{
              borderRadius: '8px',
              backgroundColor: 'white',
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200, // Set the max height for the menu
                },
              },
            }}
          >
            {countries.map((country) => (
              <MenuItem key={country} value={country}>
                {country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* <FormControl fullWidth>
      <Autocomplete
        options={country && country !== 'All' ? regions[country] : []}
        multiple
        limitTags={1}
        value={selectedRegions}
        onChange={(event, newValue) => {
          handleRegionsChange(newValue);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Region'
            variant='outlined'
            disabled={country === 'All'}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'off', // Disable browser autocomplete
              readOnly: country === 'All', // Prevent input focus and keyboard opening if country is 'All'
              // onMouseDown: (event) => event.preventDefault(), // Prevent focus on click
            }}
          />
        )}
        ListboxProps={{
          style: {
            maxHeight: '280px',
            overflow: 'auto',
          },
        }}
        sx={{
          borderRadius: '8px',
          backgroundColor: 'white',
          '& .MuiSvgIcon-root': {
            color: 'black', // This applies the black color to all SVG icons
          },
        }}
        // onOpen={(event) => {
        //   event.target.blur(); // Ensure the input is not focused when opened
        // }}
      />
    </FormControl> */}

        {/* <FormControl fullWidth>
          <Autocomplete
            options={country ? regions[country] : []}
            multiple
            limitTags={1}
            value={selectedRegions}
            onChange={(event, newValue) => {
              handleRegionsChange(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Region *"
                variant="outlined"
                disabled={!country}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off', // Disable browser autocomplete
                  readOnly: true, // Prevent input focus and keyboard opening
                  // onMouseDown: (event) => event.preventDefault(), // Prevent focus on click
                }}
              />
            )}
            ListboxProps={{
              style: {
                maxHeight: '280px',
                overflow: 'auto',
              },
            }}
            sx={{
              borderRadius: '8px',
              backgroundColor: 'white',
              '& .MuiSvgIcon-root': {
                color: 'black', // This applies the black color to all SVG icons
              },
            }}
            // onOpen={(event) => {
            //   event.target.blur(); // Ensure the input is not focused when opened
            // }}
          />
        </FormControl> */}
        <FormControl fullWidth>
          <InputLabel id="country-label">Region *</InputLabel>
          <Select
            labelId="region-label"
            label="Region *"
            id="region"
            name="region"
            multiple
            value={selectedRegions}
            onChange={(event) => {
              handleRegionsChange(event.target.value);
            }}
            sx={{
              borderRadius: '8px',
              backgroundColor: 'white',
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200, // Set the max height for the menu
                },
              },
            }}
          >
            {regions[country].map((country) => (
              <MenuItem key={country} value={country}>
                {country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ position: 'absolute', bottom: '2%', right: '5%', gap: 3 }}>
          <Button
            {...(swipeOpen && { className: 'backButton' })}
            onClick={onSwipeClose}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={(e) => {
              handleCombineclose();
            }}
            variant="contained"
            color="primary"
            disabled={
              isPosting ||
              isUpdating ||
              quillFix(description).replaceAll('<br>', '').trim().length == 0 ||
              !country ||
              selectedRegions.length === 0
            }
            sx={{ borderRadius: 2, textTransform: 'none', ml: 1 }}
          >
            {isPosting || isUpdating ? 'Submitting...' : isEdit ? 'Update' : 'Post'}
          </Button>
        </Box>
      </Box>
    );
  };

  const modules = {
  clipboard: {
    matchVisual: false, // prevents removal of multiple <br> tags or empty lines
  },
};

  return (
    <>
      {showOverlay &&
        ReactDOM.createPortal(
          <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 9999 }}>
            <SplashScreen
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 9999,
                '& > *': {
                  zIndex: 10000,
                },
              }}
            />
          </Box>,
          document.body
        )}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isSm}
        {...(isSm ? slideTransition : {})}
        transitionDuration={300}
        keepMounted
        aria-describedby="slide-up animation"
        sx={{ zIndex: 1200 }}
      >
        <DialogTitle sx={{ pl: 0, ml: 0.5 }}>
          <Grid container alignItems="center">
            <IconButton
              {...(open && !showOverlay && { className: 'backButton' })}
              onClick={onClose}
              size="small"
              sx={{ color: 'black', mr: 1 }}
            >
              <Close />
            </IconButton>
            <Avatar empCode={empCode} showStatus={true} src={user && user?.employeePhotoPath} size={35}  sx={{ img: { objectFit: 'cover', objectPosition: 'top' }}}/>
            <Button variant="text" onClick={handleButtonClick} style={{ textTransform: 'capitalize' }}>
              Anyone <ArrowDropDown />
            </Button>

            {/* {!isEdit && (
            <Grid item>
              <Button
                size="small"
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Upload Attachments
                <input type="file" multiple onChange={handleFileChange} style={{ display: 'none' }} />
              </Button>
            </Grid>
          )} */}
          </Grid>
        </DialogTitle>
        <DialogContent dividers sx={{ padding: 1 }}>
          <Stack justifyContent={'space-between'}>
            <Box component="form" noValidate autoComplete="off" border={0}>
              {/* <Grid container spacing={isSm ? 1 : 3}> */}
              {/* <Grid item md={3} sm={12} xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  label="News Title"
                  type="text"
                  fullWidth
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              </Grid> */}
              {/* <Grid item xs={4}>
                <TextField
                  margin="dense"
                  label="News Subtitle"
                  type="text"
                  fullWidth
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  variant="outlined"
                  sx={{ borderRadius: 2 }}
                />
              </Grid> */}
              {/* {!isEdit && ( */}
              {/* <Grid item md={3} sm={12} xs={12}>
                <FormControl fullWidth margin="dense" variant="outlined">
                  <Autocomplete
                    options={categoriesArray}
                    value={categoriesArray.length === 1 ? categoriesArray[0] : categories}
                    onChange={(event, newValue) => setCategories(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="News Category"
                        variant="outlined"
                        disabled={categoriesArray.length === 1}
                      />
                    )}
                    ListboxProps={{
                      style: {
                        maxHeight: '280px',
                        overflow: 'auto',
                      },
                    }}
                    sx={{ borderRadius: 2, width: '100%' }}
                  />
                </FormControl>
              </Grid> */}
              {/* )} */}

              <Grid item md={12} xl={12} sm={12} xs={12} sx={{}}>
                <Box sx={{ position: 'relative' }}>
                  <StyledQuill
                    theme="snow"
                     modules={modules}
                    onChange={(value) => handleDescriptionChange(value)}
                    placeholder="Write something intresting..."
                    sx={{
                      whiteSpace: 'pre-wrap',
                      border: 0,
                      height: 'calc(100vh - 300px)',
                      marginBottom: 2,
                      borderRadius: '4px',
                      backgroundColor: '#fff',
                    }}
                    ref={quillRef}
                  />
                  {/* Emoji Picker Icon */}
                </Box>
              </Grid>
              {/* </Grid> */}
            </Box>
            {/* Attachments */}
            {isEdit
              ? (oldAttachments?.length > 0 || filePreviews?.length > 0) && (
                  <Box sx={{ marginTop: '3rem', border: 0 }}>
                    <Typography variant="body2" gutterBottom>
                      Attachments ({attachments.length + oldAttachments.length})
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-start',
                        gap: 1,
                        flexWrap: 'wrap',
                        cursor: 'default',
                        width: '100%',
                      }}
                    >
                      {oldAttachments.map((preview, index) => (
                        <Box
                          key={index}
                          sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}
                        >
                          <Tooltip title={attachments[index]?.name || 'media name'} arrow>
                            <AttachAvatar
                              src={`${process.env.REACT_APP_PUBLIC_UPLOADS_URL}${preview}`}
                              isVideoPreview={videoFiletypes?.test(preview?.split('.').pop())}
                              autoplay={false}
                              controls={false}
                              publicCall={false}
                              variant="square"
                              sx={{ width: 50, height: 50 }}
                            />
                          </Tooltip>
                          <IconButton
                            sx={{
                              position: 'absolute',
                              right: '-15px',
                              top: '0%',
                              transform: 'translateY(-50%)',
                              background: 'lightgrey',
                              borderRadius: '50%',
                              cursor: 'default',
                            }}
                            onClick={() => handleRemoveAttachment('oldAttachments', index)}
                          >
                            <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                          </IconButton>
                        </Box>
                      ))}
                      {filePreviews.map((preview, index) => (
                        <Box
                          key={index}
                          sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}
                        >
                          <Tooltip title={attachments[index]?.name || 'media name'} arrow>
                            {preview.type == 'video' ? (
                              <video width="50" height="50">
                                <source src={preview.url} type="video/mp4" />
                              </video>
                            ) : (
                              <AttachAvatar
                                src={preview.url}
                                isVideoPreview={preview.type == 'video'}
                                autoplay={false}
                                controls={false}
                                publicCall={false}
                                variant="square"
                                sx={{ width: 50, height: 50 }}
                              />
                            )}
                          </Tooltip>
                          <IconButton
                            sx={{
                              position: 'absolute',
                              right: '-15px',
                              top: '0%',
                              transform: 'translateY(-50%)',
                              background: 'lightgrey',
                              borderRadius: '50%',
                              cursor: 'default',
                            }}
                            onClick={() => handleRemoveAttachment('newAttachments', index)}
                          >
                            <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>

                    {/* <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 1,
                          flexWrap: 'nowrap',
                          cursor: 'default',
                        }}
                      >
                        {filePreviews?.map((preview, index) => (
                          <Box
                            key={index}
                            sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}
                          >
                            <Tooltip title={preview.name} arrow>
                              <AttachAvatar
                                src={preview.url}
                                isVideoPreview={preview.type == 'video'}
                                publicCall={false}
                                variant='square'
                                sx={{ width: 100, height: 100 }}
                              />
                            </Tooltip>
                            <IconButton
                              sx={{
                                position: 'absolute',
                                right: '-15px',
                                transform: 'translateY(-100%)',
                              }}
                              onClick={() => handleRemoveAttachment(index)}
                            >
                              <Close
                                fontSize='small'
                                sx={{ backgroundColor: 'red', color: 'white', borderRadius: '50%' }}
                              />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box> */}
                  </Box>
                )
              : attachments?.length > 0 && (
                  <Box sx={{ marginTop: '3rem', border: 0 }}>
                    <Typography variant="body2" gutterBottom>
                      Attachments ({attachments.length})
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'flex-start',
                        gap: 1,
                        flexWrap: 'wrap',
                        cursor: 'default',
                      }}
                    >
                      {filePreviews.map((preview, index) => (
                        <Box
                          key={index}
                          sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}
                        >
                          <Tooltip title={attachments[index]?.name || 'media name'} arrow>
                            {preview.type == 'video' ? (
                              <video width="50" height="50">
                                <source src={preview.url} type="video/mp4" />
                              </video>
                            ) : (
                              <AttachAvatar
                                src={preview.url}
                                isVideoPreview={preview.type == 'video'}
                                autoplay={false}
                                controls={false}
                                publicCall={false}
                                variant="square"
                                sx={{ width: 50, height: 50 }}
                              />
                            )}
                          </Tooltip>
                          <IconButton
                            sx={{
                              position: 'absolute',
                              right: '-15px',
                              top: '0%',
                              transform: 'translateY(-50%)',
                              background: 'lightgrey',
                              borderRadius: '50%',
                              cursor: 'default',
                            }}
                            onClick={() => handleRemoveAttachment('newAttachments', index)}
                          >
                            <Close sx={{ fontSize: '12px', m: '-5px', color: 'black' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>

                    {/* <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '15px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          gap: 1,
                          flexWrap: 'nowrap',
                          cursor: 'default',
                        }}
                      >
                        {filePreviews?.map((preview, index) => (
                          <Box
                            key={index}
                            sx={{ display: 'flex', alignItems: 'center', mr: '10px', position: 'relative' }}
                          >
                            <Tooltip title={preview.name} arrow>
                              <AttachAvatar
                                src={preview.url}
                                isVideoPreview={preview.type == 'video'}
                                publicCall={false}
                                variant='square'
                                sx={{ width: 100, height: 100 }}
                              />
                            </Tooltip>
                            <IconButton
                              sx={{
                                position: 'absolute',
                                right: '-15px',
                                transform: 'translateY(-100%)',
                              }}
                              onClick={() => handleRemoveAttachment(index)}
                            >
                              <Close
                                fontSize='small'
                                sx={{ backgroundColor: 'red', color: 'white', borderRadius: '50%' }}
                              />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    </Box> */}
                  </Box>
                )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          {/* {!isEdit && ( */}
          <Grid item sx={{ float: 'left', width: '100%' }}>
            <Button
              size="small"
              variant="text"
              component="label"
              onClick={handleUploadClick}
              // startIcon={<CloudUpload />}
              sx={{ borderRadius: 2, textTransform: 'none', float: 'left' }}
            >
              <PermMedia />

              {isMd ? null : (
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept="image/*,video/*,audio/*"
                  style={{ display: 'none' }}
                />
              )}
              {/* <input
                type="file"
                multiple
                onChange={handleFileChange}
                style={{ display: 'none' }}
                accept="image/*, video/*"
              /> */}
            </Button>
          </Grid>
          {/* )} */}
          <Button
            {...(open && !showOverlay && { className: 'backButton' })}
            onClick={onClose}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleButtonClick}
            variant="contained"
            color="primary"
            disabled={isPosting || isUpdating || !description || description == '<p><br></p>'}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            {isPosting || isUpdating ? 'Submitting...' : isEdit ? 'Review' : 'Review'}
          </Button>
        </DialogActions>
        <SwipeEdge
          height="70vh"
          drawerBleeding={0}
          title="Modify your post"
          content={<SwipeContent />}
          sx={{ zIndex: '1300 !important' }}
        />
      </Dialog>
    </>
  );
};

export default PostNewsDialog;
